import React, { useState } from 'react';
import { BrowserRouter, Route, Routes, Navigate, useNavigate } from 'react-router-dom';
import { isAdmin } from '../../../lib/roleUtils';

// ─── Layout ───────────────────────────────────────────────────────────────────
import Layout from '../layout/Layout';

// ─── Public Pages ─────────────────────────────────────────────────────────────
import Login from '../../../pages/usermanagement/login/Login';

// ─── Protected Pages ──────────────────────────────────────────────────────────
import AddCustomerPage from '../../../pages/customer/AddCustomerPage';
import ViewCustomerPage from '../../../pages/customer/ViewCustomerPage';
import AddSupplierPage from '../../../pages/supplier/AddSupplierPage';
import ViewSupplierPage from '../../../pages/supplier/ViewSupplierPage';
import AddUserPage from '../../../pages/usermanagement/AddUserPage';
import ViewUserPage from '../../../pages/usermanagement/ViewUserPage';
import NewPackingListPage from '../../../pages/packinglist/NewPackingListPage';
import AgentManagementPage from '../../../pages/packinglist/AgentManagementPage';
import PKLDownloadPage from '../../../pages/packinglist/PKLDownloadPage';
import MGirthPklPage from '../../../pages/packinglist/MGirthPklPage';
import AcDownloadPage from '../../../pages/packinglist/AcDownloadPage';
import ForwardCoverPage from '../../../pages/forward_cover/ForwardCoverPage';
import DataManagementPage from '../../../pages/datamanagement/DataManagementPage';
import DataEntryPage from '../../../pages/datamanagement/DataEntryPage';
import DocumentUploadPage from '../../../pages/datamanagement/DocumentUploadPage';
import EwayPage from '../../../pages/datamanagement/EwayPage';
import ClearingChargesPage from '../../../pages/datamanagement/ClearingChargesPage';
import CopyDocumentPage from '../../../pages/datamanagement/CopyDocumentPage';
import FileDocumentPage from '../../../pages/datamanagement/FileDocumentPage';
import PLPage from '../../../pages/datamanagement/PLPage';
import BankDetailsPage from '../../../pages/datamanagement/BankDetailsPage';

/**
 * ComingSoonPage — Temporary placeholder for sidebar action pages.
 * Replace the <Route> element once the real page is built.
 */
const ComingSoonPage = ({ title }) => {
    const nav = useNavigate();
    return (
        <div className="d-flex flex-column align-items-center justify-content-center h-100 gap-3 text-center px-4">
            <div
                style={{
                    width: 64, height: 64, borderRadius: '50%',
                    background: 'color-mix(in srgb, var(--gtss-primary) 10%, #ffffff)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" fill="var(--gtss-primary)" viewBox="0 0 16 16">
                    <path d="M8 1a2.5 2.5 0 0 1 2.5 2.5V4h-5v-.5A2.5 2.5 0 0 1 8 1m3.5 3v-.5a3.5 3.5 0 1 0-7 0V4H1v10h14V4zM2 5h12v8H2z"/>
                </svg>
            </div>
            <h2 className="fw-bold mb-0" style={{ color: 'var(--gtss-primary)', fontSize: '1.3rem' }}>{title}</h2>
            <p className="text-muted mb-0" style={{ fontSize: '13.5px' }}>
                This page is under construction. It will be available soon.
            </p>
            <button
                className="btn btn-orange btn-sm text-uppercase fw-semibold mt-1"
                onClick={() => nav(-1)}
            >
                ← Go Back
            </button>
        </div>
    );
};

/**
 * Navigation — Root router component
 *
 * Auth pattern (simple useState — replace with context/token later):
 *   isLoggedIn = false  →  shows Login page (public route)
 *   isLoggedIn = true   →  shows Layout + child pages (protected routes)
 *
 * Protected route structure uses React Router v6 Layout Route pattern:
 *   <Route element={<Layout />}>      ← Layout renders Sidebar + <Outlet />
 *     <Route path="..." element={...} />   ← child pages render inside <Outlet />
 *   </Route>
 */
const Navigation = () => {
    // Read auth state from sessionStorage (cleared automatically on tab close)
    const [isLoggedIn, setIsLoggedIn] = useState(
        () => sessionStorage.getItem('isLoggedIn') === 'true'
    );

    const userRole = sessionStorage.getItem('userRole') ?? '';
    const defaultRedirect = isAdmin(userRole) ? '/business/agent' : '/business/data-management';

    const handleLogin = () => {
        sessionStorage.setItem('isLoggedIn', 'true');
        setIsLoggedIn(true);
    };

    const handleLogout = () => {
        sessionStorage.removeItem('isLoggedIn');
        setIsLoggedIn(false);
    };

    return (
        <BrowserRouter>
            <Routes>
                {/* ── Public Route: Login (Redirects if already logged in) ── */}
                <Route
                    path="/"
                    element={!isLoggedIn ? <Login onLogin={handleLogin} /> : <Navigate to={defaultRedirect} replace />}
                />

                {/* ── Protected Routes with Sidebar Layout ── */}
                <Route element={isLoggedIn ? <Layout onLogout={handleLogout} /> : <Navigate to="/" replace />}>
                    {/* Business */}
                    <Route path="/business/packing-list" element={<NewPackingListPage />} />
                    <Route path="/business/agent" element={<AgentManagementPage />} />
                    <Route path="/business/data-management" element={<DataManagementPage />} />
                    <Route path="/business/data-entry/:id" element={<DataEntryPage />} />
                    <Route path="/business/data-upload/:id" element={<DocumentUploadPage />} />
                    <Route path="/business/forward-cover" element={<ForwardCoverPage />} />

                    {/* ── Data Entry Action Pages (sidebar shortcuts) ── */}
                    <Route path="/business/copy-document/:id" element={<CopyDocumentPage />} />
                    <Route path="/business/clearing-charges/:id" element={<ClearingChargesPage />} />
                    <Route path="/business/eway/:id" element={<EwayPage />} />
                    <Route path="/business/pl/:id" element={<PLPage />} />
                    <Route path="/business/file-document/:id" element={<FileDocumentPage />} />
                    <Route path="/business/bank-details/:id" element={<BankDetailsPage />} />

                    {/* Customer */}
                    <Route path="/customer/add" element={<AddCustomerPage />} />
                    <Route path="/customer/view" element={<ViewCustomerPage />} />

                    {/* Supplier */}
                    <Route path="/supplier/add" element={<AddSupplierPage />} />
                    <Route path="/supplier/view" element={<ViewSupplierPage />} />

                    {/* Admin / User Management */}
                    <Route path="/admin/add-user" element={<AddUserPage />} />
                    <Route path="/admin/users" element={<ViewUserPage />} />
                </Route>

                {/* ── Protected Routes without Sidebar Layout ── */}
                <Route
                    path="/business/pkl-download/:id"
                    element={isLoggedIn ? <PKLDownloadPage /> : <Navigate to="/" replace />}
                />
                <Route
                    path="/business/mgirth-pkl-download/:id"
                    element={isLoggedIn ? <MGirthPklPage /> : <Navigate to="/" replace />}
                />
                <Route
                    path="/business/ac-download/:id"
                    element={isLoggedIn ? <AcDownloadPage /> : <Navigate to="/" replace />}
                />

                {/* ── Catch-all Redirect ── */}
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </BrowserRouter>
    );
};

export default Navigation;